/**
 * PDF→editor similarity fit from LABELED point pairs.
 *
 * Same transform model as cctv-ip-import (uniform scale + translation with an
 * optional Y flip, NO rotation) — but intentionally NOT that ICP matcher: here
 * each PDF name label is already paired with its placed camera by exact name,
 * so a closed-form least-squares fit per flip suffices. One robust refit drops
 * gross outlier pairs (mis-renamed cameras, label collisions).
 */

export interface Pt {
  x: number;
  y: number;
}

/** One labeled correspondence: PDF pt coords (Y-UP) ↔ editor (paper) coords. */
export interface SimilarityPair {
  pdf: Pt;
  editor: Pt;
}

export interface SimilarityTransform {
  s: number;
  fy: 1 | -1;
  tx: number;
  ty: number;
  /** Median residual of the final fit over inlier pairs (editor units). */
  medResidual: number;
  pairCount: number;
  inlierCount: number;
}

/** Minimum labeled pairs needed to fit the similarity reliably. */
export const MIN_SIMILARITY_PAIRS = 3;
/** A pair is an outlier when its residual exceeds this × median residual. */
const OUTLIER_MEDIAN_FACTOR = 3;
/** Keeps exact fits (median 0) from dropping every pair to float noise. */
const RESIDUAL_EPS = 1e-9;

export function applyTransform(
  t: Pick<SimilarityTransform, 's' | 'fy' | 'tx' | 'ty'>,
  p: Pt
): Pt {
  return { x: t.s * p.x + t.tx, y: t.s * (t.fy * p.y) + t.ty };
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? Infinity;
}

interface FlipFit {
  s: number;
  tx: number;
  ty: number;
  residuals: number[];
}

/** Closed-form least-squares scale + translation for a fixed Y flip. */
function fitForFlip(pairs: SimilarityPair[], fy: 1 | -1): FlipFit {
  const P = pairs.map((pr) => ({ x: pr.pdf.x, y: fy * pr.pdf.y }));
  const Q = pairs.map((pr) => pr.editor);
  let cPx = 0;
  let cPy = 0;
  let cQx = 0;
  let cQy = 0;
  for (let i = 0; i < pairs.length; i++) {
    cPx += P[i].x;
    cPy += P[i].y;
    cQx += Q[i].x;
    cQy += Q[i].y;
  }
  cPx /= pairs.length;
  cPy /= pairs.length;
  cQx /= pairs.length;
  cQy /= pairs.length;

  let num = 0;
  let den = 0;
  for (let i = 0; i < pairs.length; i++) {
    num += (P[i].x - cPx) * (Q[i].x - cQx) + (P[i].y - cPy) * (Q[i].y - cQy);
    den += (P[i].x - cPx) ** 2 + (P[i].y - cPy) ** 2;
  }
  const s = den > 0 ? num / den : 1;
  const tx = cQx - s * cPx;
  const ty = cQy - s * cPy;
  const residuals = pairs.map((pr, i) =>
    Math.hypot(s * P[i].x + tx - Q[i].x, s * P[i].y + ty - Q[i].y)
  );
  return { s, tx, ty, residuals };
}

/**
 * Fit the PDF→editor similarity from labeled pairs.
 *
 * Tries both Y orientations, keeps the one with the smaller median residual,
 * then refits once without pairs whose residual exceeds 3× that median.
 * Throws when fewer than {@link MIN_SIMILARITY_PAIRS} pairs are given.
 */
export function fitSimilarityFromPairs(
  pairs: SimilarityPair[]
): SimilarityTransform {
  if (pairs.length < MIN_SIMILARITY_PAIRS) {
    throw new Error(
      `similarity 추정에는 최소 ${MIN_SIMILARITY_PAIRS}쌍이 필요합니다 (현재 ${pairs.length}쌍)`
    );
  }

  const up = fitForFlip(pairs, 1);
  const down = fitForFlip(pairs, -1);
  const fy: 1 | -1 = median(up.residuals) <= median(down.residuals) ? 1 : -1;
  let fit = fy === 1 ? up : down;
  let inlierCount = pairs.length;

  const threshold =
    OUTLIER_MEDIAN_FACTOR * median(fit.residuals) + RESIDUAL_EPS;
  const initialResiduals = fit.residuals;
  const inliers = pairs.filter((_, i) => initialResiduals[i] <= threshold);
  if (inliers.length >= MIN_SIMILARITY_PAIRS && inliers.length < pairs.length) {
    fit = fitForFlip(inliers, fy);
    inlierCount = inliers.length;
  }

  return {
    s: fit.s,
    fy,
    tx: fit.tx,
    ty: fit.ty,
    medResidual: median(fit.residuals),
    pairCount: pairs.length,
    inlierCount,
  };
}
